import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { fmtDate } from '@/components/payroll/payrollFmt';
import { PlusCircle, Pencil, Power, Users2, AlertTriangle } from 'lucide-react';

const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });
const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const FIELD = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#E08A3C] focus:outline-none focus:ring-1 focus:ring-[#E08A3C]';
const LABEL = 'block text-sm font-semibold uppercase tracking-wide text-slate-500 mb-1';

const EMPTY = {
  payee_type: '', master_id: '', assignment_id: '', customer_id: '', location_id: '',
  cadence: '', rate_basis: '', rate: '', approved_monthly_payout: '', weekly_schedule: [],
  unit_label: '', effective_start_date: '', effective_end_date: '', notes: '',
};

const PayrollCompensation = ({ meta }) => {
  const { token } = useContext(AuthContext);
  const canPrepare = !!meta?.can_prepare;
  const [ref, setRef] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [payees, setPayees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [statusRow, setStatusRow] = useState(null);
  const [statusReason, setStatusReason] = useState('');
  const [statusSaving, setStatusSaving] = useState(false);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/payroll/compensation`, auth(token))
      .then((r) => setRows(r.data))
      .catch(() => toast.error('Failed to load compensation'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    axios.get(`${API}/payroll/reference`, auth(token)).then((r) => setRef(r.data)).catch(() => {});
    axios.get(`${API}/payroll/customers`, auth(token)).then((r) => setCustomers(r.data)).catch(() => {});
    load();
  }, [token]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // load payees when payee_type changes
  useEffect(() => {
    if (!form.payee_type) { setPayees([]); return; }
    axios.get(`${API}/payroll/payees?payee_type=${form.payee_type}`, auth(token))
      .then((r) => setPayees(r.data)).catch(() => setPayees([]));
  }, [form.payee_type, token]);

  // load assignments when personnel master changes
  useEffect(() => {
    if (!form.master_id || form.payee_type === 'vendor_company') { setAssignments([]); return; }
    axios.get(`${API}/payroll/assignments?master_id=${form.master_id}`, auth(token))
      .then((r) => setAssignments(r.data)).catch(() => setAssignments([]));
  }, [form.master_id, form.payee_type, token]);

  // load locations when customer changes
  useEffect(() => {
    if (!form.customer_id) { setLocations([]); return; }
    axios.get(`${API}/payroll/locations?customer_id=${form.customer_id}`, auth(token))
      .then((r) => setLocations(r.data)).catch(() => setLocations([]));
  }, [form.customer_id, token]);

  const openNew = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => {
    setEditId(r.id);
    setForm({
      payee_type: r.payee_type, master_id: r.master_id, assignment_id: r.assignment_id || '',
      customer_id: r.customer_id, location_id: r.location_id || '', cadence: r.cadence,
      rate_basis: r.rate_basis, rate: r.rate ?? '', approved_monthly_payout: r.approved_monthly_payout ?? '',
      weekly_schedule: r.weekly_schedule || [], unit_label: r.unit_label || '',
      effective_start_date: r.effective_start_date || '', effective_end_date: r.effective_end_date || '',
      notes: r.notes || '',
    });
    setOpen(true);
  };

  const toggleDay = (d) => setForm((f) => ({
    ...f, weekly_schedule: f.weekly_schedule.includes(d)
      ? f.weekly_schedule.filter((x) => x !== d) : [...f.weekly_schedule, d],
  }));

  const applyAssignment = (aid) => {
    set('assignment_id', aid);
    const a = assignments.find((x) => x.assignment_id === aid);
    if (a) {
      setForm((f) => ({ ...f, assignment_id: aid, customer_id: a.customer_id || f.customer_id, location_id: a.location_id || '' }));
    }
  };

  const save = async () => {
    const isMonthly = form.rate_basis === 'monthly_location';
    const payload = {
      payee_type: form.payee_type, master_id: form.master_id,
      assignment_id: form.assignment_id || null,
      customer_id: form.customer_id, location_id: form.location_id || null,
      cadence: form.cadence, rate_basis: form.rate_basis,
      rate: isMonthly ? null : (form.rate === '' ? null : Number(form.rate)),
      approved_monthly_payout: isMonthly ? (form.approved_monthly_payout === '' ? null : Number(form.approved_monthly_payout)) : null,
      weekly_schedule: isMonthly ? form.weekly_schedule : [],
      unit_label: form.unit_label || null,
      effective_start_date: form.effective_start_date,
      effective_end_date: form.effective_end_date || null,
      notes: form.notes || null,
    };
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/payroll/compensation/${editId}`, payload, auth(token));
        toast.success('Compensation updated');
      } else {
        await axios.post(`${API}/payroll/compensation`, payload, auth(token));
        toast.success('Compensation created');
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const toggleActive = (r) => { setStatusRow(r); setStatusReason(''); };

  const confirmStatus = async () => {
    if (!statusRow || !statusReason.trim()) return;
    setStatusSaving(true);
    try {
      await axios.patch(`${API}/payroll/compensation/${statusRow.id}/status`,
        { is_active: !statusRow.is_active, status_reason: statusReason.trim() }, auth(token));
      toast.success(`Compensation ${statusRow.is_active ? 'deactivated' : 'reactivated'}`);
      setStatusRow(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setStatusSaving(false); }
  };

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.payee_name || '').toLowerCase().includes(s) || (r.account_name || '').toLowerCase().includes(s)
      || String(r.account_number || '').toLowerCase().includes(s);
  });

  const rateDisplay = (r) => r.rate_basis === 'monthly_location'
    ? `${money(r.approved_monthly_payout)} / mo` : money(r.rate);
  const basisLabel = (v) => ref?.rate_bases.find((x) => x.value === v)?.label || v;
  const cadenceLabel = (v) => (ref?.cadences.find((x) => x.value === v)?.label || v).split(' (')[0];
  const payeeTypeLabel = (v) => ref?.payee_types.find((x) => x.value === v)?.label || v;

  const canSubmit = form.payee_type && form.master_id && form.customer_id && form.cadence
    && form.rate_basis && form.effective_start_date
    && (form.rate_basis === 'monthly_location'
      ? (form.approved_monthly_payout && form.weekly_schedule.length > 0)
      : (form.rate !== '' && Number(form.rate) > 0));

  return (
    <div data-testid="payroll-compensation">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#0E2A47]">Compensation</h2>
          <p className="text-sm text-slate-500">How each payee is paid per assignment — references master records, no duplication.</p>
        </div>
        {canPrepare && (
          <Button onClick={openNew} data-testid="comp-new-btn" className="bg-[#E08A3C] hover:brightness-95">
            <PlusCircle size={16} className="mr-2" /> New Compensation
          </Button>
        )}
      </div>

      <div className="mb-3">
        <Input placeholder="Search payee, account name or number…" value={search}
          onChange={(e) => setSearch(e.target.value)} data-testid="comp-search" className="max-w-sm" />
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm" data-testid="comp-table">
          <thead className="bg-slate-50 text-left text-sm font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Person/Company</th><th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Acct #</th><th className="px-4 py-3">Account / Location</th>
              <th className="px-4 py-3">Pay Schedule</th><th className="px-4 py-3">Pay Method</th>
              <th className="px-4 py-3 text-right">Rate / Payout</th><th className="px-4 py-3">Effective</th>
              <th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-12 text-center text-slate-400" data-testid="comp-empty">
                No compensation configured yet.{canPrepare && ' Click “New Compensation” to add the first.'}
              </td></tr>
            )}
            {!loading && filtered.map((r) => (
              <tr key={r.id} className={`hover:bg-slate-50 ${!r.is_active ? 'opacity-55' : ''}`} data-testid={`comp-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-[#0E2A47]">{r.payee_name}</div>
                  <div className="text-sm text-slate-400">{r.payee_identifier}</div>
                </td>
                <td className="px-4 py-3 text-slate-600">{payeeTypeLabel(r.payee_type)}</td>
                <td className="px-4 py-3 font-mono text-slate-700">{r.account_number || '—'}</td>
                <td className="px-4 py-3">
                  <div className="text-slate-700">{r.account_name}</div>
                  {r.location_name && <div className="text-sm text-slate-400">{r.location_name}</div>}
                </td>
                <td className="px-4 py-3 text-slate-600">{cadenceLabel(r.cadence)}</td>
                <td className="px-4 py-3 text-slate-600">{basisLabel(r.rate_basis)}</td>
                <td className="px-4 py-3 text-right font-medium text-[#0E2A47]">{rateDisplay(r)}</td>
                <td className="px-4 py-3 text-sm text-slate-500">{fmtDate(r.effective_start_date)}{r.effective_end_date ? ` → ${fmtDate(r.effective_end_date)}` : ''}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full border px-2 py-0.5 text-sm font-semibold ${r.is_active ? 'border-[#8FC93A] bg-[#8FC93A]/15 text-[#4d7c0f]' : 'border-slate-300 bg-slate-100 text-slate-500'}`}>
                    {r.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {canPrepare && (
                    <div className="flex justify-end gap-1">
                      <button onClick={() => openEdit(r)} data-testid={`comp-edit-${r.id}`} className="rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-[#E08A3C]"><Pencil size={15} /></button>
                      <button onClick={() => toggleActive(r)} data-testid={`comp-toggle-${r.id}`} className="rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-red-500"><Power size={15} /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="comp-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit Compensation' : 'New Compensation'}</DialogTitle>
            <DialogDescription>
              {editId ? 'Update pay schedule, rate/payout and effective dates. The person/company and account cannot be changed.'
                      : 'Configure how a person or company is paid for an account/location. References existing master records.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={LABEL}>Person/Company Type</label>
              <select className={FIELD} data-testid="comp-payee-type" value={form.payee_type}
                disabled={!!editId}
                onChange={(e) => setForm((f) => ({ ...f, payee_type: e.target.value, master_id: '', assignment_id: '' }))}>
                <option value="">Select…</option>
                {ref?.payee_types.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Person/Company</label>
              <select className={FIELD} data-testid="comp-master" value={form.master_id} disabled={!!editId || !form.payee_type}
                onChange={(e) => set('master_id', e.target.value)}>
                <option value="">Select…</option>
                {payees.map((p) => <option key={p.master_id} value={p.master_id}>{p.name} ({p.identifier})</option>)}
              </select>
            </div>

            {form.payee_type && form.payee_type !== 'vendor_company' && assignments.length > 0 && (
              <div className="col-span-2">
                <label className={LABEL}>Link to existing assignment (optional)</label>
                <select className={FIELD} data-testid="comp-assignment" value={form.assignment_id}
                  onChange={(e) => applyAssignment(e.target.value)}>
                  <option value="">None</option>
                  {assignments.map((a) => <option key={a.assignment_id} value={a.assignment_id}>{a.customer_name}{a.location_name ? ` / ${a.location_name}` : ''} · {a.assignment_role}</option>)}
                </select>
              </div>
            )}

            <div>
              <label className={LABEL}>Account (Customer)</label>
              <select className={FIELD} data-testid="comp-customer" value={form.customer_id} disabled={!!editId}
                onChange={(e) => setForm((f) => ({ ...f, customer_id: e.target.value, location_id: '' }))}>
                <option value="">Select…</option>
                {customers.map((c) => <option key={c.customer_id} value={c.customer_id}>{c.account_number ? `${c.account_number} · ` : ''}{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Service Location (optional)</label>
              <select className={FIELD} data-testid="comp-location" value={form.location_id} disabled={!!editId || !form.customer_id}
                onChange={(e) => set('location_id', e.target.value)}>
                <option value="">— Account level —</option>
                {locations.map((l) => <option key={l.location_id} value={l.location_id}>{l.location_name}</option>)}
              </select>
            </div>

            <div>
              <label className={LABEL}>Pay Schedule</label>
              <select className={FIELD} data-testid="comp-cadence" value={form.cadence} onChange={(e) => set('cadence', e.target.value)}>
                <option value="">Select…</option>
                {ref?.cadences.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Pay Method</label>
              <select className={FIELD} data-testid="comp-rate-basis" value={form.rate_basis} onChange={(e) => set('rate_basis', e.target.value)}>
                <option value="">Select…</option>
                {ref?.rate_bases.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            {form.rate_basis === 'monthly_location' ? (
              <>
                <div>
                  <label className={LABEL}>Approved Monthly Payout</label>
                  <Input type="number" step="0.01" data-testid="comp-payout" value={form.approved_monthly_payout}
                    onChange={(e) => set('approved_monthly_payout', e.target.value)} placeholder="e.g. 500.00" />
                </div>
                <div className="col-span-2">
                  <label className={LABEL}>Weekly Work Schedule (drives normal labor-day auto-count)</label>
                  <div className="flex flex-wrap gap-2" data-testid="comp-weekdays">
                    {ref?.weekdays.map((d) => (
                      <button type="button" key={d.value} onClick={() => toggleDay(d.value)}
                        data-testid={`comp-weekday-${d.value}`}
                        className={`rounded-lg border px-3 py-1.5 text-sm font-medium ${form.weekly_schedule.includes(d.value) ? 'border-[#E08A3C] bg-[#E08A3C]/15 text-[#E08A3C]' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                        {d.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            ) : form.rate_basis && (
              <>
                <div>
                  <label className={LABEL}>Rate ({basisLabel(form.rate_basis)})</label>
                  <Input type="number" step="0.01" data-testid="comp-rate" value={form.rate}
                    onChange={(e) => set('rate', e.target.value)} placeholder="0.00" />
                </div>
                {form.rate_basis === 'flat_unit' && (
                  <div>
                    <label className={LABEL}>Unit Label (optional)</label>
                    <Input data-testid="comp-unit-label" value={form.unit_label}
                      onChange={(e) => set('unit_label', e.target.value)} placeholder="e.g. per strip & wax" />
                  </div>
                )}
              </>
            )}

            <div>
              <label className={LABEL}>Effective Start</label>
              <Input type="date" data-testid="comp-start" value={form.effective_start_date} onChange={(e) => set('effective_start_date', e.target.value)} />
            </div>
            <div>
              <label className={LABEL}>Effective End (optional)</label>
              <Input type="date" data-testid="comp-end" value={form.effective_end_date} onChange={(e) => set('effective_end_date', e.target.value)} />
            </div>
            <div className="col-span-2">
              <label className={LABEL}>Notes</label>
              <Input data-testid="comp-notes" value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Optional" />
            </div>
          </div>

          {!canSubmit && (
            <div className="flex items-center gap-2 text-sm text-amber-600"><AlertTriangle size={13} /> Complete payee, account, pay schedule, pay method, rate/payout and start date.</div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="comp-cancel">Cancel</Button>
            <Button onClick={save} disabled={!canSubmit || saving} data-testid="comp-save" className="bg-[#E08A3C] hover:brightness-95">
              {saving ? 'Saving…' : (editId ? 'Save Changes' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activate / deactivate reason modal */}
      <Dialog open={!!statusRow} onOpenChange={(o) => !o && setStatusRow(null)}>
        <DialogContent className="max-w-md" data-testid="comp-status-dialog">
          <DialogHeader>
            <DialogTitle>{statusRow?.is_active ? 'Deactivate' : 'Reactivate'} Compensation</DialogTitle>
            <DialogDescription>
              {statusRow ? `${statusRow.payee_name} · ${statusRow.account_name}` : ''} — a reason is required and recorded in history.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className={LABEL}>Reason</label>
            <Input data-testid="comp-status-reason" value={statusReason}
              onChange={(e) => setStatusReason(e.target.value)} placeholder="e.g. Assignment ended" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusRow(null)} data-testid="comp-status-cancel">Cancel</Button>
            <Button onClick={confirmStatus} disabled={!statusReason.trim() || statusSaving}
              data-testid="comp-status-confirm"
              className={statusRow?.is_active ? 'bg-red-500 hover:bg-red-600' : 'bg-[#8FC93A] hover:brightness-95'}>
              {statusSaving ? 'Saving…' : (statusRow?.is_active ? 'Deactivate' : 'Reactivate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayrollCompensation;
